import { cva, type VariantProps } from 'class-variance-authority';
import { PanelLeftCloseIcon, PanelLeftOpenIcon, RocketIcon } from 'lucide-react';
import { Slot } from 'radix-ui';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ComponentProps,
  type FC,
  type ReactNode,
} from 'react';
import { cn } from '../lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';

interface SectionNavContextValue {
  collapsed: boolean;
  toggle: () => void;
}

const SectionNavContext = createContext<SectionNavContextValue>({
  collapsed: false,
  toggle: () => undefined,
});

export const useSectionNav = () => useContext(SectionNavContext);

/**
 * Section-nav persistence key, the counterpart of the app nav's
 * `solink-nav-sidebar-open`. Surfaces that must collapse independently pass
 * their own `storageKey`, or the second surface restores the first one's state.
 */
const SECTION_NAV_COLLAPSED_STORAGE_KEY = 'solink-section-nav-collapsed';

function readSectionNavCollapsed(storageKey: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    // private mode / quota — fall through
  }
  return fallback;
}

function writeSectionNavCollapsed(storageKey: string, collapsed: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, String(collapsed));
  } catch {
    // ignore
  }
}

export const SectionNavProvider: FC<{
  /** Used only on the first visit, before the rail has a stored state. */
  defaultCollapsed?: boolean;
  storageKey?: string;
  children?: ReactNode;
}> = ({ defaultCollapsed = false, storageKey = SECTION_NAV_COLLAPSED_STORAGE_KEY, children }) => {
  const [collapsed, setCollapsed] = useState(() =>
    readSectionNavCollapsed(storageKey, defaultCollapsed),
  );

  const toggle = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      writeSectionNavCollapsed(storageKey, next);
      return next;
    });
  }, [storageKey]);

  const value = useMemo(() => ({ collapsed, toggle }), [collapsed, toggle]);

  return <SectionNavContext.Provider value={value}>{children}</SectionNavContext.Provider>;
};

/**
 * Collapse timing, copied from the app rail (`NAV_WIDTH_MS` + the curve in
 * `nav-sidebar.css`) so a page showing both rails animates them as one gesture.
 */
export const SECTION_NAV_TRANSITION =
  'duration-200 [transition-timing-function:cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none';

export type SectionNavProps = ComponentProps<'nav'>;

/**
 * In-page vertical section list: a column of icon + label rows, optionally with
 * a right-aligned count. Use it where a page switches between many peer
 * sections and a horizontal `Tabs` strip would overflow (Settings sections,
 * Events categories).
 *
 * Rows share the vocabulary of `NavSidebarItem` — elevated pill plus accent
 * glyph when active — but none of the app rail's machinery: no
 * `SidebarProvider` context, no collapse rail, no `--desktopNavWidth` coupling.
 * It is plain in-flow layout the page owns, so it can sit beside `NavSidebar`
 * without either one reaching into the other.
 *
 * `SectionNavToggle` collapses the column to an icon rail (same idea as
 * `NavSidebarToggle`), on the app rail's 200ms curve. Wrap the nav and any
 * sibling that needs `useSectionNav` (for example a content-column title) in
 * `SectionNavProvider`. Width is `w-60` expanded and `w-12` collapsed; override
 * with `className` when the page's content column needs the room — and give any
 * sibling that resizes with the rail `SECTION_NAV_TRANSITION` so the two edges
 * travel together.
 */
export const SectionNav: FC<SectionNavProps> = ({ className, children, ...props }) => {
  const { collapsed } = useSectionNav();

  return (
    <nav
      data-slot="section-nav"
      data-collapsed={collapsed ? 'icon' : 'expanded'}
      className={cn(
        // Clip on x so labels can stay in flow (and keep animating) while the
        // rail is narrower than they are.
        'flex shrink-0 flex-col gap-0.5 overflow-y-auto overflow-x-hidden py-1',
        'transition-[width,padding]',
        SECTION_NAV_TRANSITION,
        collapsed ? 'w-12 items-center px-1' : 'w-60 px-2',
        className,
      )}
      {...props}
    >
      {children}
    </nav>
  );
};

const sectionNavItemVariants = cva(
  [
    'flex w-full cursor-pointer items-center gap-2 rounded-sm px-3 py-2 text-left',
    'outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
    // Matches NavSidebarItem: glyphs are foreground-secondary, labels primary.
    '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-foreground-secondary [&_svg]:stroke-current',
    'transition-[background-color,color,gap,padding,width,height]',
    SECTION_NAV_TRANSITION,
  ],
  {
    variants: {
      isActive: {
        // Gray elevated pill + teal glyph, as in NavSidebarItem and Aspen
        // mobile's DrawerNavItem. Hover stays at elevated/60 so it reads as
        // below active in both themes.
        true: 'bg-background-elevated [&_svg]:text-foreground-accent',
        false: 'hover:bg-background-elevated/60',
      },
      disabled: {
        true: 'text-foreground-muted [&_svg]:text-foreground-muted',
        false: 'text-foreground-primary',
      },
      collapsed: {
        // 32x32 tile around the 16px glyph, as in `NavSidebarItem`, so both
        // rails read as the same control when collapsed. Drop the gap so the
        // squeezed label cannot push the glyph off centre.
        true: 'size-8 justify-center gap-0 p-2',
        false: '',
      },
    },
    defaultVariants: { isActive: false, disabled: false, collapsed: false },
  },
);

export interface SectionNavItemProps
  extends
    Omit<ComponentProps<'button'>, 'disabled'>,
    Omit<VariantProps<typeof sectionNavItemVariants>, 'collapsed'> {
  /** Render as the child element — pass a router link here. */
  asChild?: boolean;
  /** Label shown in a tooltip while collapsed. */
  tooltip?: string;
}

export const SectionNavItem: FC<SectionNavItemProps> = ({
  asChild = false,
  isActive = false,
  disabled = false,
  tooltip,
  className,
  onClick,
  ...props
}) => {
  const { collapsed } = useSectionNav();
  const Comp = asChild ? Slot.Root : 'button';

  const item = (
    <Comp
      type={!asChild ? 'button' : undefined}
      data-slot="section-nav-item"
      data-active={isActive}
      aria-current={isActive ? 'page' : undefined}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
      className={cn(sectionNavItemVariants({ isActive, disabled, collapsed }), className)}
      onClick={(event) => {
        if (disabled) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        onClick?.(event);
      }}
      {...props}
    />
  );

  if (!tooltip) return item;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{item}</TooltipTrigger>
      <TooltipContent side="right" align="center" hidden={!collapsed}>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * Item text. Truncates so a long section name cannot push the count off-row.
 *
 * Collapsing squeezes the label to zero width instead of hiding it: `hidden`
 * would drop it at the first frame and leave the rail animating around a gap.
 */
export const SectionNavItemLabel: FC<ComponentProps<'span'>> = ({ className, ...props }) => {
  const { collapsed } = useSectionNav();

  return (
    <span
      data-slot="section-nav-item-label"
      className={cn(
        'min-w-0 flex-1 truncate label-md',
        'transition-[max-width,opacity]',
        SECTION_NAV_TRANSITION,
        collapsed ? 'max-w-0 opacity-0' : 'max-w-full opacity-100',
        className,
      )}
      {...props}
    />
  );
};

/** Trailing count, as already formatted text (e.g. `192.3k`). */
export const SectionNavItemCount: FC<ComponentProps<'span'>> = ({ className, ...props }) => {
  const { collapsed } = useSectionNav();

  return (
    <span
      data-slot="section-nav-item-count"
      className={cn(
        'shrink-0 overflow-hidden tabular-nums text-foreground-secondary label-sm',
        'transition-[max-width,opacity]',
        SECTION_NAV_TRANSITION,
        collapsed ? 'max-w-0 opacity-0' : 'max-w-full opacity-100',
        className,
      )}
      {...props}
    />
  );
};

/** Collapse control — `PanelLeftClose` / `PanelLeftOpen`, same as `NavSidebarToggle`. */
export const SectionNavToggle: FC<
  Omit<ComponentProps<'button'>, 'onClick'> & {
    expandLabel?: string;
    collapseLabel?: string;
  }
> = ({
  className,
  expandLabel = 'Expand sections',
  collapseLabel = 'Collapse sections',
  ...props
}) => {
  const { collapsed, toggle } = useSectionNav();

  return (
    <button
      type="button"
      data-slot="section-nav-toggle"
      onClick={toggle}
      aria-label={collapsed ? expandLabel : collapseLabel}
      aria-expanded={!collapsed}
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-sm',
        'outline-hidden text-foreground-secondary transition-colors',
        'hover:bg-background-elevated/60 focus-visible:ring-2 focus-visible:ring-ring',
        '[&_svg]:size-4 [&_svg]:shrink-0',
        className,
      )}
      {...props}
    >
      {collapsed ? <PanelLeftOpenIcon /> : <PanelLeftCloseIcon />}
    </button>
  );
};

/**
 * Bottom-pinned footer (build version, collapse control). Expanded it is one
 * row; collapsed it stacks, so the version keeps its place above the toggle.
 */
export const SectionNavFooter: FC<ComponentProps<'div'>> = ({ className, ...props }) => {
  const { collapsed } = useSectionNav();

  return (
    <div
      data-slot="section-nav-footer"
      className={cn(
        'mt-auto flex items-center gap-1 py-1',
        collapsed ? 'flex-col justify-center px-0' : 'px-2',
        className,
      )}
      {...props}
    />
  );
};

/**
 * Version / hint text in the footer. Collapsed, the rail has no room for the
 * string, so it becomes a rocket glyph that carries the same text in a tooltip.
 */
export const SectionNavFooterText: FC<ComponentProps<'span'>> = ({
  className,
  children,
  ...props
}) => {
  const { collapsed } = useSectionNav();

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-slot="section-nav-footer-text"
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-sm',
              'text-foreground-secondary [&_svg]:size-4 [&_svg]:shrink-0',
              className,
            )}
            {...props}
          >
            <RocketIcon />
            <span className="sr-only">{children}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="right" align="center">
          {children}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <span
      data-slot="section-nav-footer-text"
      className={cn(
        'min-w-0 flex-1 truncate font-normal text-foreground-secondary label-sm',
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
};
